<template>
  <div class="p-interest-form__form-container">
    <div class="e-tabs e-tabs--interest">
      <ul>
        <li
          class="e-tabs__tab"
          :class="{ 'e-tabs__tab--active': !showConfirmation }">
          <span>{{ dictionary.formTabLabel || 'Form' }}</span>
        </li>
        <li
          class="e-tabs__tab"
          :class="{ 'e-tabs__tab--active': showConfirmation }">
          <span>{{ dictionary.otpTabLabel || 'Verification' }}</span>
        </li>
      </ul>
    </div>

    <form
      v-if="!showConfirmation"
      autocomplete="off"
      @submit.prevent="onSubmitForm">
      <div class="b-block b-block--transparent b-block--sm-pad">
        <Loader v-if="isLoading"></Loader>
        <Alert
          v-if="!isLoading && errorMsgs.length"
          key="submit-errors"
          dismiss-by-native=""
          type="danger"
          :messages="errorMsgs"
          @close="removeErrors"></Alert>
        <!-- Group 1: Movie Selection -->
        <div class="form-group">
          <h3 class="form-group-title">{{ dictionary.group1Title }}</h3>

          <div class="form-floating">
            <div
              class="movie-selector"
              :class="{
                'movie-selector--error': v$.movie.$error,
                'movie-selector--selected': selectedMovieData,
              }"
              tabindex="0"
              @click="openMovieModal"
              @blur="v$.movie.$touch">
              <div v-if="selectedMovieData" class="movie-selector__content">
                <div class="movie-selector__image">
                  <img
                    :src="selectedMovieData.thumb"
                    :alt="selectedMovieData.title" />
                </div>
                <div class="movie-selector__text">
                  <div class="movie-selector__title">
                    {{ selectedMovieData.title }}
                  </div>
                  <div
                    v-if="selectedMovieData.subTitle"
                    class="movie-selector__subtitle">
                    {{ selectedMovieData.subTitle }}
                  </div>
                </div>
              </div>
              <div v-else class="movie-selector__placeholder">
                <span>{{ dictionary.moviePlaceholder }}</span>
              </div>
            </div>

            <!-- validations -->
            <template v-if="v$.movie.$error">
              <div
                v-if="v$.movie.required.$invalid"
                class="field-validation-error">
                {{ dictionary.movieRequired }}
              </div>
            </template>
          </div>
        </div>

        <!-- Group 2: Date and Cinema -->
        <div class="form-group">
          <h3 class="form-group-title">{{ dictionary.group2Title }}</h3>

          <div class="form-floating">
            <Datepicker
              v-model="date"
              uid="visitDate"
              auto-apply
              name="date"
              :placeholder="dictionary.datePlaceholder"
              :enable-time-picker="false"
              :locale="configuration.locale"
              format="dd/MM/yyyy"
              model-type="dd/MM/yyyy"
              autocomplete="off"
              dark
              :min-date="minVisitDate"
              :max-date="maxVisitDate"
              :year-range="[
                minVisitDate.getFullYear(),
                maxVisitDate.getFullYear(),
              ]"
              :hide-navigation="['month', 'year']"
              :timezone="{
                convertModel: false,
                exactMatch: true,
                timezone: 'Europe/Athens',
              }"
              :input-class-name="
                v$.date.$error
                  ? 'form-control form-control--error'
                  : 'form-control'
              " />

            <label for="dp-input-visitDate" class="form-label">
              {{ dictionary.dateLabel }}
            </label>

            <!-- validations -->
            <template v-if="v$.date.$error">
              <div
                v-if="v$.date.required.$invalid"
                class="field-validation-error">
                {{ dictionary.dateRequired }}
              </div>
              <div
                v-if="v$.date.minDate.$invalid"
                class="field-validation-error">
                {{ dictionary.dateMinError }}
              </div>
            </template>
          </div>

          <div class="form-floating">
            <select
              id="time"
              v-model.trim="time"
              name="time"
              class="form-select"
              :class="{ 'form-select--error': v$.time.$error }"
              @blur="v$.time.$touch">
              <option value="" disabled>
                {{ dictionary.timePlaceholder }}
              </option>
              <option
                v-for="item in timeOptions"
                :key="item.value"
                :value="item.value"
                :selected="item.selected">
                {{ item.display }}
              </option>
            </select>

            <label for="time" class="form-label">
              {{ dictionary.timeLabel }}
            </label>

            <!-- validations -->
            <template v-if="v$.time.$error">
              <div
                v-if="v$.time.required.$invalid"
                class="field-validation-error">
                {{ dictionary.timeRequired }}
              </div>
            </template>
          </div>

          <div class="form-floating">
            <select
              id="cinema"
              v-model.trim="cinema"
              name="cinema"
              class="form-select"
              :class="{ 'form-select--error': v$.cinema.$error }"
              @blur="v$.cinema.$touch">
              <option value="" disabled>
                {{ dictionary.cinemaPlaceholder }}
              </option>
              <option
                v-for="item in filters.cinemas"
                :key="item.value"
                :value="item.value"
                :disabled="!item.isOnline">
                {{ item.display }}
              </option>
            </select>

            <label for="cinema" class="form-label">
              {{ dictionary.cinemaLabel }}
            </label>

            <!-- validations -->
            <template v-if="v$.cinema.$error">
              <div
                v-if="v$.cinema.required.$invalid"
                class="field-validation-error">
                {{ dictionary.cinemaRequired }}
              </div>
            </template>
          </div>
        </div>

        <!-- Group 3: School Information -->
        <div class="form-group">
          <h3 class="form-group-title">{{ dictionary.group3Title }}</h3>

          <div class="form-floating">
            <input
              id="school"
              v-model.trim="school"
              name="school"
              type="text"
              class="form-control"
              :placeholder="dictionary.schoolPlaceholder"
              :class="{ 'form-control--error': v$.school.$error }"
              @blur="v$.school.$touch" />

            <label for="school" class="form-label">
              {{ dictionary.schoolLabel }}
            </label>

            <!-- validations -->
            <template v-if="v$.school.$error">
              <div
                v-if="v$.school.required.$invalid"
                class="field-validation-error">
                {{ dictionary.schoolRequired }}
              </div>
            </template>
          </div>

          <div class="form-floating">
            <input
              id="numberOfStudents"
              v-model.trim="numberOfStudents"
              name="numberOfStudents"
              type="number"
              class="form-control"
              min="1"
              :placeholder="dictionary.numberOfStudentsPlaceholder"
              :class="{ 'form-control--error': v$.numberOfStudents.$error }"
              @blur="v$.numberOfStudents.$touch" />

            <label for="numberOfStudents" class="form-label">
              {{ dictionary.numberOfStudentsLabel }}
            </label>

            <!-- validations -->
            <template v-if="v$.numberOfStudents.$error">
              <div
                v-if="v$.numberOfStudents.required.$invalid"
                class="field-validation-error">
                {{ dictionary.numberOfStudentsRequired }}
              </div>
            </template>
          </div>
        </div>

        <!-- Group 4: Contact Information -->
        <div class="form-group">
          <h3 class="form-group-title">{{ dictionary.group4Title }}</h3>

          <div class="form-floating">
            <input
              id="name"
              v-model.trim="name"
              name="name"
              type="text"
              class="form-control"
              :placeholder="dictionary.namePlaceholder"
              :class="{ 'form-control--error': v$.name.$error }"
              @blur="v$.name.$touch" />

            <label for="name" class="form-label">
              {{ dictionary.nameLabel }}
            </label>

            <!-- validations -->
            <template v-if="v$.name.$error">
              <div
                v-if="v$.name.required.$invalid"
                class="field-validation-error">
                {{ dictionary.nameRequired }}
              </div>
            </template>
          </div>

          <div class="form-floating">
            <input
              id="lastName"
              v-model.trim="lastName"
              name="lastName"
              type="text"
              class="form-control"
              :placeholder="dictionary.lastNamePlaceholder"
              :class="{ 'form-control--error': v$.lastName.$error }"
              @blur="v$.lastName.$touch" />

            <label for="lastName" class="form-label">
              {{ dictionary.lastNameLabel }}
            </label>

            <!-- validations -->
            <template v-if="v$.lastName.$error">
              <div
                v-if="v$.lastName.required.$invalid"
                class="field-validation-error">
                {{ dictionary.lastNameRequired }}
              </div>
            </template>
          </div>

          <div class="d-flex" style="column-gap: 0.5em">
            <div class="form-col" style="width: 30%">
              <div class="form-floating">
                <select
                  id="dialingCode"
                  v-model.trim="dialingCode"
                  name="dialingCode"
                  class="form-select"
                  :class="{ 'form-select--error': v$.dialingCode.$error }"
                  @blur="v$.dialingCode.$touch">
                  <option value="" disabled>
                    {{ dictionary.dialingCodePlaceholder }}
                  </option>
                  <option
                    v-for="item in countryOptions"
                    :key="item"
                    :value="item">
                    {{ item }}
                  </option>
                </select>

                <label for="dialingCode" class="form-label">
                  {{ dictionary.dialingCodeLabel }}
                </label>

                <!-- validations -->
                <template v-if="v$.dialingCode.$error">
                  <div
                    v-if="v$.dialingCode.required.$invalid"
                    class="field-validation-error">
                    {{ dictionary.dialingCodeRequired }}
                  </div>
                </template>
              </div>
            </div>
            <div class="form-col" style="flex: 1">
              <div class="form-floating">
                <input
                  id="phone"
                  v-model.trim="phone"
                  name="phone"
                  type="text"
                  class="form-control"
                  :placeholder="dictionary.phonePlaceholder"
                  :class="{ 'form-control--error': v$.phone.$error }"
                  @blur="v$.phone.$touch" />

                <label for="phone" class="form-label">
                  {{ dictionary.phoneLabel }}
                </label>

                <!-- validations -->
                <template v-if="v$.phone.$error">
                  <div
                    v-if="v$.phone.required.$invalid"
                    class="field-validation-error">
                    {{ dictionary.phoneRequired }}
                  </div>
                </template>
              </div>
            </div>
          </div>

          <div class="form-floating">
            <input
              id="email"
              v-model.trim="email"
              name="email"
              type="email"
              class="form-control"
              :placeholder="dictionary.emailPlaceholder"
              :class="{ 'form-control--error': v$.email.$error }"
              formnovalidate
              @blur="v$.email.$touch" />

            <label for="email" class="form-label">
              {{ dictionary.emailLabel }}
            </label>

            <!-- validations -->
            <template v-if="v$.email.$error">
              <div
                v-if="v$.email.email.$invalid"
                class="field-validation-error">
                {{ dictionary.emailRegex }}
              </div>
              <div
                v-if="v$.email.required.$invalid"
                class="field-validation-error">
                {{ dictionary.emailRequired }}
              </div>
            </template>
          </div>

          <div class="form-floating">
            <textarea
              id="message"
              v-model.trim="message"
              name="message"
              class="form-control"
              :placeholder="dictionary.messagePlaceholder"></textarea>

            <label for="message" class="form-label">
              {{ dictionary.messageLabel }}
            </label>
          </div>
        </div>

        <div class="p-interest-form__form-message">
          {{ dictionary.fieldRequired }}
        </div>
      </div>

      <!-- Consent Checkbox -->
      <div class="p-interest-form__consent">
        <div class="e-checkbox e-checkbox--consent">
          <input
            id="consent"
            v-model="consent"
            type="checkbox"
            name="consent"
            @change="v$.consent.$touch()" />

          <label for="consent">
            <span class="e-checkbox__text">{{ dictionary.consentLabel }}</span>
          </label>

          <!-- validations -->
          <template v-if="v$.consent.$error">
            <div
              v-if="v$.consent.sameAs.$invalid"
              class="field-validation-error">
              {{ dictionary.consentRequired }}
            </div>
          </template>
        </div>
      </div>

      <div class="b-block__form-btn">
        <button
          type="submit"
          class="p-interest-form__submit-btn"
          :disabled="submitButtonIsDisabled">
          <span>{{ dictionary.buttonSubmitText }}</span>
        </button>
      </div>
    </form>

    <ConfirmationForm
      v-if="showConfirmation"
      :first-name="name"
      :last-name="lastName"
      :email="email"
      :mobile-telephone="phone"
      :selected-country-code="dialingCode"
      :country-options="countryOptions"
      @changed-phone="handlePhoneChange"
      @changed-country-code="handleCountryCodeChange"
      @changed-email="handleEmailChange" />

    <!-- Movie Selection Modal -->
    <ModalOverlay
      v-model="showMovieModal"
      size="player"
      background-class="b-pop-up__holder movie-selection-modal"
      header-background-class="b-pop-up__title-holder movie-selection-modal__header">
      <template #header>
        <div class="b-pop-up__header__title">
          {{ dictionary.movieLabel }}
        </div>
      </template>
      <template #body>
        <div class="b-pop-up__content movie-modal-content">
          <!-- Tabs and Search -->
          <div class="movie-modal-header">
            <!-- Tabs -->
            <div v-if="movieTabs && movieTabs.length > 0" class="e-tabs">
              <ul>
                <li
                  v-for="tab in movieTabs"
                  :key="tab.value"
                  class="e-tabs__tab"
                  :class="{
                    'e-tabs__tab--active': activeMovieTab === tab.value,
                  }"
                  @click="selectMovieTab(tab.value)">
                  <span>{{ tab.display }}</span>
                </li>
              </ul>
            </div>

            <!-- Search Input -->
            <div
              class="b-search-input b-search-input--no-btn movie-modal-search">
              <input
                v-model="searchMovieTitle"
                class="e-search"
                type="text"
                :placeholder="
                  dictionary.movieSearchPlaceholder || 'Αναζήτηση Ταινίας'
                "
                @input="handleMovieSearch" />
            </div>
          </div>

          <!-- Movie Cards Grid - Desktop -->
          <div class="movie-modal-grid movie-modal-grid--desktop">
            <div
              v-for="record in displayedMovies"
              :key="record.id"
              :class="{
                'movie-modal-card--selected': movie === record.id,
              }"
              @click="selectMovie(record)">
              <MovieCard
                :no-redirect="true"
                :image="record.thumb"
                :title="record.title"
                :subtitle="record.subTitle"
                :pg="record.pg || ''"
                :duration="record.dur || 0"
                :sphera="record.sphera"
                :dolby="record.dolby"
                :three-dimensions="record.threeDimensions"
                :imax="record.imax"
                :imax3-d="record.imax3D"
                :transl="record.transl"
                :only-members="record.onlyMembers"
                :play="false"
                :is-booking-flow="true"
                :elementary-school="record.elementarySchool"
                :middle-school="record.middleSchool"
                :high-school="record.highSchool"
                :elements-show-options="{
                  showDesc: false,
                  showGenres: true,
                  showDuration: true,
                  showBookingCta: true,
                  posterCardDimensions: {},
                }"
                :dictionary="dictionary" />
            </div>
          </div>

          <!-- Movie Cards List - Mobile -->
          <div class="movie-modal-list movie-modal-list--mobile">
            <div
              v-for="record in displayedMovies"
              :key="record.id"
              :class="{
                'movie-modal-list-item': true,
                'movie-modal-list-item--selected': movie === record.id,
              }"
              @click="selectMovie(record)">
              <div class="movie-modal-list-item__poster">
                <img :src="record.thumb" :alt="record.title" />
              </div>
              <div class="movie-modal-list-item__content">
                <div class="movie-modal-list-item__title b-movie-card__title">
                  {{ record.title }}
                </div>
                <div
                  v-if="record.subTitle"
                  class="movie-modal-list-item__subtitle b-movie-card__subtitle">
                  {{ record.subTitle }}
                </div>
                <div class="movie-modal-list-item__info b-movie-attr">
                  <div v-if="record.pg" class="e-k">
                    {{ record.pg }}
                  </div>
                  <!-- <div v-if="record.genre" class="movie-attr--movie">
                    <span>
                      {{ record.genre }}
                    </span>
                  </div> -->
                  <div v-if="record.dur" class="movie-attr--time">
                    <span>{{ record.dur }}</span>
                  </div>
                </div>
                <div class="movie-modal-list-item__tags b-movie-card__labels">
                  <div
                    v-if="record.elementarySchool"
                    class="e-label e-label--elementary-school">
                    <span>{{ dictionary.elementarySchool }}</span>
                  </div>
                  <div
                    v-if="record.middleSchool"
                    class="e-label e-label--middle-school">
                    <span>{{ dictionary.middleSchool }}</span>
                  </div>
                  <div
                    v-if="record.highSchool"
                    class="e-label e-label--high-school">
                    <span>{{ dictionary.highSchool }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </ModalOverlay>
  </div>
</template>

<script>
import * as lib from '../../lib/lib'
import { email, required, sameAs } from '@vuelidate/validators'
import useVuelidate from '@vuelidate/core'
import Loader from '../components/Shared/Loader.vue'
import ModalOverlay from '../components/ModalOverlay.vue'
import Alert from '../components/Shared/Alert.vue'
import MovieCard from '../components/MovieCard.vue'
import ConfirmationForm from '../components/RegisterForm/ConfirmationForm.vue'
import postRequestHandler from '../../lib/postRequestHandler'
import getErrorMessage from '../../lib/getErrorMessage'
import Datepicker from '@vuepic/vue-datepicker'
import { useBookingStore } from '../store/BookingStore'

const DEFAULT_COUNTRY_CODE = '30'

export default {
  components: {
    Loader,
    ModalOverlay,
    Alert,
    MovieCard,
    ConfirmationForm,
    Datepicker,
  },
  setup() {
    return { v$: useVuelidate(), store: useBookingStore() }
  },
  data() {
    return {
      isLoading: true,
      errorMsgs: [],
      showMovieModal: false,
      // form properties
      // Group 1
      movie: '',
      moviesRecords: [],
      selectedMovieData: null,
      movieTabs: [],
      activeMovieTab: null,
      allMoviesRecords: [],
      displayedMovies: [],
      searchMovieTitle: '',
      // Group 2
      minVisitDate: new Date(
        new Date().getFullYear(),
        new Date().getMonth(),
        new Date().getDate() + 15,
      ),
      maxVisitDate: new Date(
        new Date().getFullYear() + 5,
        new Date().getMonth(),
        new Date().getDate(),
      ),
      date: '',
      cinema: '',
      time: '',
      timeOptions: [],
      filters: {},
      // Group 3
      school: '',
      numberOfStudents: '',
      // Group 4
      name: '',
      lastName: '',
      dialingCode: '',
      phone: '',
      email: '',
      message: '',
      // Consent
      consent: false,
      showConfirmation: false,
      countryOptions: [],
    }
  },
  validations() {
    return {
      // Group 1
      movie: {
        required,
      },
      // Group 2
      date: {
        required,
        minDate: (value) => {
          // Let required validator handle empty value
          if (!value) {
            return true
          }

          // Parse DD/MM/YYYY format
          const parts = value.split('/')
          if (parts.length !== 3) {
            return false
          }

          const day = parseInt(parts[0], 10)
          // Month is 0-indexed in JavaScript Date
          const month = parseInt(parts[1], 10) - 1
          const year = parseInt(parts[2], 10)

          // Validate date parts
          if (isNaN(day) || isNaN(month) || isNaN(year)) {
            return false
          }
          if (year < 1000 || year > 9999) {
            return false
          }

          const inputDate = new Date(year, month, day)
          const today = new Date()
          // Reset time to start of day
          today.setHours(0, 0, 0, 0)

          return inputDate > today
        },
      },
      time: {
        required,
      },
      cinema: {
        required,
      },
      // Group 3
      school: {
        required,
      },
      numberOfStudents: {
        required,
      },
      // Group 4
      name: {
        required,
      },
      lastName: {
        required,
      },
      dialingCode: {
        required,
      },
      phone: {
        required,
      },
      email: {
        required,
        email,
      },
      consent: {
        sameAs: sameAs(true),
      },
    }
  },

  computed: {
    submitButtonIsDisabled() {
      return this.isLoading || this.v$.$invalid
    },
  },

  async mounted() {
    lib.checkCaptcha(() => (this.isLoading = false))
    const { filters, records, timeOptions, countryOptions } = lib.getData(
      'interestFormAppData',
    )

    // Initialize filters and records
    this.filters = filters || {}
    this.moviesRecords = records || []
    this.allMoviesRecords = records || []
    this.displayedMovies = records || []
    this.timeOptions = timeOptions || []
    this.countryOptions = countryOptions || []

    // Fetch userSessionId from API
    await this.fetchUserSessionId()

    // Set default dialingCode to DEFAULT_COUNTRY_CODE if it exists in countryOptions
    if (countryOptions && countryOptions.length > 0) {
      const defaultOption = countryOptions.find(
        (option) => option === DEFAULT_COUNTRY_CODE,
      )
      if (defaultOption) {
        this.dialingCode = DEFAULT_COUNTRY_CODE
      }
    }

    // Get tabs from filters
    const tabsData = filters?.tabs || []

    // Check if there's only one unique tab value across all records
    const uniqueTabValues = new Set()
    records.forEach((movie) => {
      if (movie.tabs && Array.isArray(movie.tabs)) {
        movie.tabs.forEach((tab) => uniqueTabValues.add(tab))
      }
    })

    // Initialize tabs
    if (tabsData && tabsData.length > 0 && uniqueTabValues.size > 1) {
      // Multiple unique tab values exist, show tabs
      this.movieTabs = tabsData
      this.activeMovieTab = tabsData[0].value
      this.applyMovieTabFilter(tabsData[0].value)
    } else {
      // No tabs available or only one unique tab value, show all movies
      this.movieTabs = []
      this.activeMovieTab = null
    }

    this.setPreselectedOptions()
    this.$nextTick(() => {
      const urlParams = new URLSearchParams(window.location.search)
      const movieId = urlParams.get('movieId')
      if (movieId) {
        const movieEntry = this.moviesRecords.find((i) => i.id === movieId)
        if (movieEntry) {
          this.movie = movieEntry.id
          this.selectedMovieData = movieEntry
        }
      }
    })
  },

  methods: {
    async fetchUserSessionId() {
      try {
        const response = await fetch(
          this.configuration.endpointGetUserSession,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          },
        )

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const result = await response.json()

        if (!result.hasError && result.data?.userSessionId) {
          const userSessionId = result.data.userSessionId
          if (this.store && this.store.setUsersessionId) {
            console.log('Setting userSessionId in store:', userSessionId)
            this.store.setUsersessionId(userSessionId)
          }
        }
      } catch (error) {
        console.error('Failed to fetch user session:', error)
      }
    },
    removeErrors() {
      this.errorMsgs = []
    },
    setPreselectedOptions() {
      // movie
      if (this.moviesRecords) {
        const movieEntry = this.moviesRecords.find((i) => i.selected)
        if (movieEntry) {
          this.movie = movieEntry.id
          this.selectedMovieData = movieEntry
        }
      }

      // cinemas
      if (this.filters && this.filters.cinemas) {
        const cinemaEntry = this.filters.cinemas.find((i) => i.selected)
        if (cinemaEntry) {
          this.cinema = cinemaEntry.value
        }
      }
    },

    openMovieModal() {
      this.showMovieModal = true
    },

    selectMovie(movieRecord) {
      this.movie = movieRecord.id
      this.selectedMovieData = movieRecord
      this.showMovieModal = false
      this.v$.movie.$touch()
    },

    selectMovieTab(tabId) {
      this.activeMovieTab = tabId
      this.searchMovieTitle = ''
      this.applyMovieTabFilter(tabId)
    },

    applyMovieTabFilter(tabValue) {
      const currentTab = this.movieTabs.find((tab) => tab.value === tabValue)

      if (!currentTab) {
        this.displayedMovies = this.allMoviesRecords
        return
      }

      // Filter movies based on tab value matching record's tabs array
      const tabMovies = this.allMoviesRecords.filter(
        (movie) => movie.tabs && movie.tabs.includes(tabValue),
      )

      this.displayedMovies = tabMovies
    },

    handleMovieSearch() {
      // Clear search if input is empty
      if (!this.searchMovieTitle.trim()) {
        // Reapply current tab filter
        if (this.activeMovieTab) {
          this.applyMovieTabFilter(this.activeMovieTab)
        } else {
          this.displayedMovies = this.allMoviesRecords
        }
        return
      }

      // Get the base movies (either from active tab or all movies)
      let baseMovies = this.allMoviesRecords
      if (this.activeMovieTab) {
        baseMovies = this.allMoviesRecords.filter(
          (movie) => movie.tabs && movie.tabs.includes(this.activeMovieTab),
        )
      }

      // Filter based on search input
      const searchResults = baseMovies.filter((movie) =>
        movie.title.toLowerCase().includes(this.searchMovieTitle.toLowerCase()),
      )

      this.displayedMovies = searchResults
    },

    resetUserData() {
      this.v$.$reset()
      // Group 1
      this.movie = ''
      this.selectedMovieData = null
      // Group 2
      this.date = ''
      this.cinema = ''
      // Group 3
      this.school = ''
      this.numberOfStudents = ''
      // Group 4
      this.name = ''
      this.lastName = ''
      this.dialingCode = ''
      this.phone = ''
      this.email = ''
      this.message = ''
    },

    handlePhoneChange(newPhone) {
      this.phone = newPhone
    },

    handleCountryCodeChange(newCode) {
      this.dialingCode = newCode
    },

    handleEmailChange(newEmail) {
      this.email = newEmail
    },

    async onSubmitForm() {
      this.v$.$validate()

      if (this.isLoading) {
        return false
      }

      if (this.v$.$invalid) {
        await this.$nextTick()
        lib.scrollToValidation()
        return false
      }

      this.removeErrors()

      await this.finalSubmit()
    },

    async finalSubmit() {
      this.isLoading = true

      // Get movie title from selectedMovieData
      const movieTitle = this.selectedMovieData?.title

      // Get cinema name from filters
      const cinemaData = this.filters?.cinemas?.find(
        (c) => c.value === this.cinema,
      )
      const cinemaName = cinemaData?.display

      const postData = {
        // Group 1
        movie: movieTitle,
        // Group 2
        date: this.date,
        time: this.time,
        cinema: cinemaName,
        // Group 3
        school: this.school,
        numberOfStudents: String(this.numberOfStudents),
        // Group 4
        name: this.name,
        lastName: this.lastName,
        dialingCode: this.dialingCode,
        phone: this.phone,
        email: this.email,
        message: this.message,
        language: document.documentElement.lang,
      }

      // Store the form data in the store
      this.store.setInterestFormData(postData)
      this.store.setOTPFormData({
        email: this.email,
        dialingCode: this.dialingCode,
        phone: this.phone,
      })

      // Submit initial data to backend
      const submitPayload = {
        email: this.email,
        userSessionId: this.store.bookingProgress.usersessionId,
      }

      console.log(
        'endpointRequestEmailOtp:',
        this.configuration.endpointRequestEmailOtp,
      )

      await postRequestHandler({
        postData: submitPayload,
        endpoint: this.configuration.endpointRequestEmailOtp,
        successCallback: this.handleSuccess,
        errorCallback: this.handleError,
        refreshTokenHandler: this.configuration.refresh,
        captchaAction: this.configuration.captchaAction,
      })
    },

    handleSuccess() {
      this.isLoading = false
      this.removeErrors()
      this.showConfirmation = true
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },

    async handleError(messages) {
      this.isLoading = false

      this.errorMsgs = getErrorMessage(
        messages,
        this.dictionary.defaultErrorMessage,
      )

      await this.$nextTick()
      lib.scrollToError()
    },
  },
}
</script>
